import type { Caption, CaptionWord } from "../../types";

/**
 * ElevenLabs word shape (subset) as returned by Speech-to-Text.
 */
export interface ElevenLabsTranscriptWord {
  text: string;
  start: number; // seconds
  end: number; // seconds
  type: "word" | "spacing" | "audio_event" | string;
  disabled?: boolean;
  logprob?: number;
}

export interface ElevenLabsTranscript {
  words?: ElevenLabsTranscriptWord[];
  text?: string;
  language_code?: string;
}

export interface GroupWordsOptions {
  /** Gap between words (seconds) that triggers a new caption group */
  timeThreshold?: number;
  /** If true, punctuation at end of a word triggers a new caption group */
  usePunctuation?: boolean;
  /** Max duration (seconds) before forcing a new caption group */
  maxGroupDuration?: number;
}

/**
 * Mirror reelcat-v1 behavior:
 * - Keep only real words (`type === "word"`) and not disabled
 * - Group by: gaps, punctuation, and max duration
 * - Preserve word-level timestamps exactly (converted to ms)
 */
export function elevenLabsTranscriptToCaptions(
  transcript: ElevenLabsTranscript,
  options: GroupWordsOptions = {}
): Caption[] {
  const {
    timeThreshold = 0.5,
    usePunctuation = true,
    maxGroupDuration = 2.0,
  } = options;

  const words = transcript.words ?? [];
  const validWords = words.filter(
    (w) =>
      w.type === "word" &&
      !(w.disabled ?? false) &&
      typeof w.start === "number" &&
      typeof w.end === "number"
  );

  if (validWords.length === 0) return [];

  // Group words -> groups of words (in seconds)
  const groups: Array<Array<ElevenLabsTranscriptWord>> = [];
  let currentGroup: Array<ElevenLabsTranscriptWord> = [validWords[0]];

  for (let i = 1; i < validWords.length; i++) {
    const prev = validWords[i - 1];
    const curr = validWords[i];

    const gap = curr.start - prev.end;
    const groupStart = currentGroup[0].start;
    const groupDuration = prev.end - groupStart;

    let shouldNewGroup = false;

    if (gap > timeThreshold) shouldNewGroup = true;
    if (usePunctuation && /[.!?]$/.test(prev.text)) shouldNewGroup = true;
    if (groupDuration > maxGroupDuration) shouldNewGroup = true;

    if (shouldNewGroup) {
      groups.push(currentGroup);
      currentGroup = [curr];
    } else {
      currentGroup.push(curr);
    }
  }

  groups.push(currentGroup);

  // Convert to Caption[] (ms)
  return groups.map((group) => {
    const startMs = Math.round(group[0].start * 1000);
    const endMs = Math.round(group[group.length - 1].end * 1000);

    const captionWords: CaptionWord[] = group.map((w) => ({
      word: w.text,
      startMs: Math.round(w.start * 1000),
      endMs: Math.round(w.end * 1000),
      confidence: typeof w.logprob === "number" ? Math.max(0, Math.min(1, Math.exp(w.logprob))) : 0.95,
    }));

    return {
      text: group.map((w) => w.text).join(" "),
      startMs,
      endMs,
      timestampMs: null,
      confidence: 0.95,
      words: captionWords,
    };
  });
}


